/**
 * Things that stop light: a screen, which catches a real image, and a stop,
 * which cuts part of a beam.
 *
 * A screen is transparent to the probes that FIND images — it shows an image,
 * it does not decide where one is. A stop is not: the rays it blocks are gone.
 */
import { PartKind, numbered } from '../registry';
import { box, localToWorld } from '../shapes';
import { Part, Surface, Vec2 } from '../types';
import { toWorld } from '../vec';

export interface ScreenData { height: number; [key: string]: unknown }
export interface StopData { opening: number; offset: number; height: number; [key: string]: unknown }

export const screen: PartKind<ScreenData> = {
    key: 'screen',
    label: 'Screen',
    tag: 'Sc',
    freedom: { onAxis: true, turns: true },
    create: (at: Vec2, id: string): Part<ScreenData> => ({
        id, kind: 'screen', at: { x: at.x, y: 0 }, angle: 0, mount: 'axis', data: { height: 20 },
    }),
    surfaces: (p, w): Surface[] => [{
        id: `${p.id}:face`, partId: p.id, probeTransparent: true,
        shape: {
            form: 'segment',
            a: toWorld(p.at, p.angle, { x: 0, y: -p.data.height / 2 }),
            b: toWorld(p.at, p.angle, { x: 0, y: p.data.height / 2 }),
        },
        rule: { law: 'absorb' },
        name: numbered(p as Part, w, 'Sc'),
    }],
    outline: (p) => localToWorld(p as Part, box(0, 0.2, p.data.height / 2)),
    controls: () => [
        { type: 'number', key: 'height', label: 'Height', unit: 'cm', min: 5, max: 60, step: 1 },
    ],
    name: (p, w) => numbered(p as Part, w, 'Sc'),
};

export const stop: PartKind<StopData> = {
    key: 'stop',
    label: 'Stop',
    tag: 'St',
    freedom: { onAxis: true, turns: true },
    create: (at: Vec2, id: string): Part<StopData> => ({
        id, kind: 'stop', at: { x: at.x, y: 0 }, angle: 0, mount: 'axis',
        data: { opening: 4, offset: 0, height: 30 },
    }),
    /** Two blades, above and below the opening. */
    surfaces(p, w): Surface[] {
        const { opening, offset, height } = p.data;
        const top = offset + opening / 2, bottom = offset - opening / 2;
        const blade = (i: number, y0: number, y1: number): Surface => ({
            id: `${p.id}:blade${i}`, partId: p.id,
            shape: { form: 'segment', a: toWorld(p.at, p.angle, { x: 0, y: y0 }), b: toWorld(p.at, p.angle, { x: 0, y: y1 }) },
            rule: { law: 'absorb' },
            name: numbered(p as Part, w, 'St'),
        });
        return [blade(0, top, height / 2), blade(1, -height / 2, bottom)];
    },
    outline: (p) => localToWorld(p as Part, box(0, 0.2, p.data.height / 2)),
    controls: () => [
        { type: 'number', key: 'opening', label: 'Opening', unit: 'cm', min: 0.5, max: 30, step: 0.5 },
        { type: 'number', key: 'offset', label: 'Offset', unit: 'cm', min: -15, max: 15, step: 0.5 },
    ],
    name: (p, w) => numbered(p as Part, w, 'St'),
};
